import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2, ArrowUp, ArrowDown, Hash, Lock, Loader2, Users, User, Eye, EyeOff } from "lucide-react";
import MembersModal from "@/components/chat/MembersModal";

const GROUPS = [
  { value: "general", label: "Général", desc: "visible par tous" },
  { value: "admin", label: "Administration", desc: "Admin + Direction" },
  { value: "prive", label: "Privé", desc: "membres choisis" },
];
const ACCESS = [
  { value: "all", label: "Écriture libre" },
  { value: "admin", label: "Lecture seule" },
];
const ROLE_SHORT = {
  admin: "Admin", dirigeant: "Direction", manager_commercial: "Manager",
  vendeur: "Vendeur", secretaire_bo: "Secrétaire", responsable_facturation: "Factu",
};

export default function ChannelManager({ channels, staff = [], onClose, onChanged }) {
  const [group, setGroup] = useState("general");
  const [name, setName] = useState("");
  const [postAccess, setPostAccess] = useState("all");
  const [newMembers, setNewMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [membersFor, setMembersFor] = useState(null);
  const [dmSalons, setDmSalons] = useState([]);

  const loadDms = () =>
    api.get("/chat/admin/dm-salons").then((r) => setDmSalons(r.data)).catch(() => setDmSalons([]));
  useEffect(() => {
    if (group === "prive") loadDms();
  }, [group]);

  const moveDm = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= dmSalons.length) return;
    const arr = [...dmSalons];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setDmSalons(arr);
    try { await api.put("/chat/admin/dm-salons/order", { vids: arr.map((d) => d.vid) }); onChanged(); }
    catch { toast.error("Réorganisation impossible"); loadDms(); }
  };

  const toggleHideDm = async (d) => {
    try {
      await api.patch(`/chat/admin/dm-salons/${d.vid}`, { hidden: !d.hidden });
      setDmSalons((s) => s.map((x) => (x.vid === d.vid ? { ...x, hidden: !x.hidden } : x)));
      onChanged();
    } catch { toast.error("Modification impossible"); }
  };

  const list = useMemo(
    () => channels.filter((c) => c.section === group && c.type === "channel"),
    [channels, group]
  );
  const pickable = staff.filter((u) => !["admin", "dirigeant"].includes(u.role));

  const create = async () => {
    if (!name.trim()) { toast.error("Donne un nom au salon"); return; }
    setSaving(true);
    try {
      await api.post("/chat/admin/channels", {
        name: name.trim(), group, post_access: postAccess,
        member_ids: group === "prive" ? newMembers : undefined,
      });
      setName(""); setPostAccess("all"); setNewMembers([]);
      toast.success("Salon créé");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Création impossible"); }
    finally { setSaving(false); }
  };

  const patch = async (c, body) => {
    try { await api.patch(`/chat/admin/channels/${c.id}`, body); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Modification impossible"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer le salon « ${c.name} » et tous ses messages ?`)) return;
    try { await api.delete(`/chat/admin/channels/${c.id}`); toast.success("Salon supprimé"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    const ids = list.map((c) => c.id);
    [ids[idx], ids[target]] = [ids[target], ids[idx]];
    try { await api.post("/chat/admin/channels/reorder", { ordered_ids: ids }); onChanged(); }
    catch { toast.error("Réorganisation impossible"); }
  };

  const toggleNew = (id) => setNewMembers((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="channel-manager-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <h2 className="font-semibold text-[#111111]">Gérer les salons</h2>
          <button onClick={onClose} data-testid="channel-manager-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 pt-4">
          <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden mb-4">
            {GROUPS.map((g) => (
              <button key={g.value} onClick={() => setGroup(g.value)} data-testid={`cm-group-${g.value}`}
                className={`px-3 py-1.5 text-xs font-semibold ${group === g.value ? "bg-[#111111] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {g.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-2">
          {list.map((c, idx) => (
            <div key={c.id} className="flex items-center gap-2 py-2 border-b border-slate-50" data-testid={`cm-channel-${c.id}`}>
              <div className="flex flex-col">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-slate-300 hover:text-slate-600 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === list.length - 1} className="text-slate-300 hover:text-slate-600 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
              </div>
              {group === "prive" ? <Users className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : c.post_access === "admin" ? <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <Hash className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
              <input defaultValue={c.name} onBlur={(e) => e.target.value.trim() && e.target.value !== c.name && patch(c, { name: e.target.value.trim() })}
                data-testid={`cm-name-${c.id}`} className="flex-1 text-sm px-2 py-1 border border-transparent hover:border-slate-200 focus:border-slate-300 rounded-sm focus:outline-none" />
              {group === "prive" && (
                <button onClick={() => setMembersFor({ channelId: c.id, title: c.name })} data-testid={`cm-members-${c.id}`}
                  className="flex items-center gap-1 text-xs text-slate-600 hover:text-[#B8873A] border border-slate-200 rounded-sm px-2 py-1">
                  <Users className="w-3.5 h-3.5" /> Membres
                </button>
              )}
              <select value={c.post_access} onChange={(e) => patch(c, { post_access: e.target.value })} data-testid={`cm-access-${c.id}`}
                className="text-xs border border-slate-200 rounded-sm px-1.5 py-1 bg-white">
                {ACCESS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
              {c.is_system ? (
                <span className="text-[10px] text-slate-300 px-1">système</span>
              ) : (
                <button onClick={() => remove(c)} data-testid={`cm-delete-${c.id}`} className="text-slate-300 hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
          {list.length === 0 && dmSalons.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">Aucun salon dans ce groupe</div>}

          {group === "prive" && dmSalons.length > 0 && (
            <>
              <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider pt-3 pb-1">Salons privés vendeurs</div>
              {dmSalons.map((d, idx) => (
                <div key={d.id} className={`flex items-center gap-2 py-2 border-b border-slate-50 ${d.hidden ? "opacity-50" : ""}`} data-testid={`cm-dm-${d.id}`}>
                  <div className="flex flex-col">
                    <button onClick={() => moveDm(idx, -1)} disabled={idx === 0} className="text-slate-300 hover:text-slate-600 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => moveDm(idx, 1)} disabled={idx === dmSalons.length - 1} className="text-slate-300 hover:text-slate-600 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                  </div>
                  <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span className="flex-1 text-sm text-slate-700 truncate px-1">{d.name}{d.hidden && <span className="text-[10px] text-slate-400 ml-1">(masqué)</span>}</span>
                  <button onClick={() => setMembersFor({ channelId: d.id, title: d.name, lockedId: d.vid })} data-testid={`cm-dm-members-${d.id}`}
                    className="flex items-center gap-1 text-xs text-slate-600 hover:text-[#B8873A] border border-slate-200 rounded-sm px-2 py-1">
                    <Users className="w-3.5 h-3.5" /> Membres
                  </button>
                  <button onClick={() => toggleHideDm(d)} data-testid={`cm-dm-hide-${d.id}`} title={d.hidden ? "Afficher" : "Masquer"}
                    className="text-slate-400 hover:text-[#B8873A] p-1">
                    {d.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-4 bg-slate-50">
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
            Nouveau salon {GROUPS.find((g) => g.value === group)?.label.toLowerCase()}
          </div>
          <div className="flex items-center gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du salon" data-testid="cm-new-name"
              className="flex-1 text-sm px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#111111]" />
            <select value={postAccess} onChange={(e) => setPostAccess(e.target.value)} data-testid="cm-new-access"
              className="text-xs border border-slate-300 rounded-sm px-2 py-2 bg-white">
              {ACCESS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
            <button onClick={create} disabled={saving} data-testid="cm-create-btn"
              className="px-3 py-2 bg-[#111111] text-white rounded-sm text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Créer
            </button>
          </div>
          {group === "prive" && (
            <div className="mt-3">
              <div className="text-[11px] text-slate-500 mb-1.5">Membres (Admin + Direction toujours inclus) — {newMembers.length} sélectionné{newMembers.length > 1 ? "s" : ""}</div>
              <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-sm bg-white divide-y divide-slate-50" data-testid="cm-new-members">
                {pickable.map((u) => {
                  const on = newMembers.includes(u.id);
                  return (
                    <button key={u.id} onClick={() => toggleNew(u.id)} data-testid={`cm-new-member-${u.id}`}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50">
                      <span className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${on ? "bg-[#111111] border-[#111111]" : "border-slate-300"}`}>
                        {on && <span className="w-1.5 h-1.5 bg-white rounded-[1px]" />}
                      </span>
                      <span className="flex-1 truncate text-slate-700">{u.name}</span>
                      <span className="text-[10px] text-slate-400">{ROLE_SHORT[u.role] || u.role}</span>
                    </button>
                  );
                })}
                {pickable.length === 0 && <div className="px-3 py-3 text-xs text-slate-400">Aucun utilisateur</div>}
              </div>
            </div>
          )}
        </div>
      </div>

      {membersFor && (
        <MembersModal channelId={membersFor.channelId} title={membersFor.title} staff={staff} lockedId={membersFor.lockedId}
          onClose={() => setMembersFor(null)} onSaved={onChanged} />
      )}
    </div>
  );
}
